import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  CUSTOMER_TAG_COLOURS,
  CUSTOMER_TAG_MAX_LENGTH,
  type CustomerTagColour,
  type CustomerTagDefinition,
} from '@tailoring-crm/shared';
import {
  useCreateCustomerTag,
  useCustomerTags,
  useDeleteCustomerTag,
  useUpdateCustomerTag,
} from '../../lib/customers/use-customers.js';
import { TAG_COLOUR_CLASSES } from '../../lib/customers/tag-colours.js';
import { formatDate } from '../../lib/format.js';
import {
  cn,
  Button,
  useConfirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Manage Tags: the shop's tag list, in a sheet from the right.
 *
 * Tags are made here before anybody wears one, each with a colour, up to the shop's limit. A
 * rename or a delete reaches every customer carrying the tag; a colour change reaches only the
 * tag. `onRenamed` and `onRemoved` let the list keep its filter pointing at tags that exist.
 */
export function ManageCustomerTagsSheet({
  open,
  onOpenChange,
  onRenamed,
  onRemoved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRenamed?: (from: string, to: string) => void;
  onRemoved?: (tag: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const query = useCustomerTags();
  const tags = query.data?.tags ?? [];
  const limit = query.data?.limit ?? 0;
  const full = tags.length >= limit;
  const [adding, setAdding] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setAdding(false);
        onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          'inset-0 h-dvh max-h-none rounded-none',
          'sm:inset-y-0 sm:left-auto sm:right-0 sm:top-0 sm:h-full sm:max-w-3xl sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:rounded-l-xl',
        )}
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle>{t('customers.tags.manageTitle')}</DialogTitle>
            <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300">
              {t('customers.title')}
            </span>
          </div>
          <DialogDescription>{t('customers.tags.manageDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{t('customers.tags.heading')}</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {t('customers.tags.used')}{' '}
              <span className="font-semibold text-foreground">
                {tags.length}/{limit}
              </span>
            </span>
            <Button
              size="sm"
              onClick={() => setAdding(true)}
              disabled={full || adding || query.isPending}
              title={full ? t('customers.tags.limitReached', { limit }) : undefined}
            >
              <Plus aria-hidden="true" />
              {t('customers.tags.add')}
            </Button>
          </div>
        </div>
        {full ? (
          <p className="text-xs text-muted-foreground">
            {t('customers.tags.limitReached', { limit })}
          </p>
        ) : null}

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('customers.tags.column.name')}</th>
                <th className="px-3 py-2 font-medium">{t('customers.tags.column.colour')}</th>
                <th className="px-3 py-2 font-medium">{t('customers.tags.column.count')}</th>
                <th className="px-3 py-2 font-medium">{t('customers.tags.column.modified')}</th>
                <th className="px-3 py-2">
                  <span className="sr-only">{t('customers.tags.column.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {adding ? <NewTagRow taken={tags} onDone={() => setAdding(false)} /> : null}
              {tags.map((tag) => (
                <TagRow key={tag.id} tag={tag} onRenamed={onRenamed} onRemoved={onRemoved} />
              ))}
              {tags.length === 0 && !adding ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {t('customers.tags.none')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The API's refusal, in words the row can show beside the box that caused it. */
function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function TagChip({
  name,
  colour,
  className,
}: {
  name: string;
  colour: CustomerTagColour;
  className?: string;
}): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center truncate rounded-full px-2.5 py-0.5 text-xs font-medium',
        TAG_COLOUR_CLASSES[colour].chip,
        className,
      )}
    >
      {name}
    </span>
  );
}

/** The palette as a row of swatches; the chosen one carries a tick. */
function ColourPalette({
  value,
  onChange,
}: {
  value: CustomerTagColour;
  onChange: (colour: CustomerTagColour) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="radiogroup"
      aria-label={t('customers.tags.column.colour')}
      className="flex flex-wrap gap-1.5"
    >
      {CUSTOMER_TAG_COLOURS.map((colour) => (
        <button
          key={colour}
          type="button"
          role="radio"
          aria-checked={colour === value}
          aria-label={t(`customers.tags.colour.${colour}`)}
          onClick={() => onChange(colour)}
          className={cn(
            'flex size-6 items-center justify-center rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            TAG_COLOUR_CLASSES[colour].dot,
            colour === value && 'ring-2 ring-foreground/60 ring-offset-2',
          )}
        >
          {colour === value ? <Check aria-hidden="true" className="size-3.5 text-white" /> : null}
        </button>
      ))}
    </div>
  );
}

/** A swatch that opens the palette. Used on each existing row. */
function ColourSwatch({
  tag,
  disabled,
  onChange,
}: {
  tag: CustomerTagDefinition;
  disabled: boolean;
  onChange: (colour: CustomerTagColour) => void;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={t('customers.tags.recolourAction', { tag: tag.name })}
          className={cn(
            'size-5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            TAG_COLOUR_CLASSES[tag.colour].dot,
          )}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <ColourPalette
          value={tag.colour}
          onChange={(colour) => {
            setOpen(false);
            if (colour !== tag.colour) onChange(colour);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function NewTagRow({
  taken,
  onDone,
}: {
  taken: readonly CustomerTagDefinition[];
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const create = useCreateCustomerTag();
  const [name, setName] = useState('');
  const [colour, setColour] = useState<CustomerTagColour>(
    () =>
      CUSTOMER_TAG_COLOURS.find((one) => !taken.some((tag) => tag.colour === one)) ??
      CUSTOMER_TAG_COLOURS[0],
  );
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('customers.tags.nameRequired'));
      return;
    }
    try {
      await create.mutateAsync({ name: trimmed, colour });
      toast({ title: t('customers.tags.created', { tag: trimmed }), variant: 'success' });
      onDone();
    } catch (failure) {
      setError(messageOf(failure, t('error.unexpected')));
    }
  };

  return (
    <tr>
      <td colSpan={5} className="px-3 py-3">
        <form className="flex flex-col gap-3" onSubmit={(event) => void onSubmit(event)} noValidate>
          <div className="flex flex-col gap-1">
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setError(null);
              }}
              maxLength={CUSTOMER_TAG_MAX_LENGTH}
              placeholder={t('customers.tags.namePlaceholder')}
              aria-label={t('customers.tags.newName')}
              aria-invalid={error ? true : undefined}
              autoFocus
              className="h-9 max-w-xs"
            />
            {error ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            ) : null}
          </div>
          <ColourPalette value={colour} onChange={setColour} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={create.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </td>
    </tr>
  );
}

function TagRow({
  tag,
  onRenamed,
  onRemoved,
}: {
  tag: CustomerTagDefinition;
  onRenamed?: (from: string, to: string) => void;
  onRemoved?: (tag: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const update = useUpdateCustomerTag();
  const remove = useDeleteCustomerTag();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.name);
  const [error, setError] = useState<string | null>(null);
  const pending = update.isPending || remove.isPending;

  const stopEditing = (): void => {
    setEditing(false);
    setDraft(tag.name);
    setError(null);
  };

  const onRename = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const to = draft.trim().replace(/\s+/g, ' ');
    if (!to || to === tag.name) {
      stopEditing();
      return;
    }
    try {
      await update.mutateAsync({ tagId: tag.id, patch: { name: to } });
      toast({
        title: t('customers.tags.renamed', { from: tag.name, to, count: tag.customerCount }),
        variant: 'success',
      });
      setEditing(false);
      onRenamed?.(tag.name, to);
    } catch (failure) {
      setError(messageOf(failure, t('error.unexpected')));
    }
  };

  const onRecolour = async (colour: CustomerTagColour): Promise<void> => {
    try {
      await update.mutateAsync({ tagId: tag.id, patch: { colour } });
    } catch (failure) {
      toast({ title: messageOf(failure, t('error.unexpected')), variant: 'destructive' });
    }
  };

  const onDelete = async (): Promise<void> => {
    const ok = await confirm({
      title: t('customers.tags.removeTitle', { tag: tag.name }),
      description: t('customers.tags.removeDescription', { count: tag.customerCount }),
      confirmLabel: t('customers.tags.removeConfirm'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(tag.id);
      toast({ title: t('customers.tags.removed', { tag: tag.name }), variant: 'success' });
      onRemoved?.(tag.name);
    } catch (failure) {
      toast({ title: messageOf(failure, t('error.unexpected')), variant: 'destructive' });
    }
  };

  return (
    <tr className="group">
      <td className="px-3 py-2 align-middle">
        {editing ? (
          <form className="flex flex-col gap-1" onSubmit={(event) => void onRename(event)}>
            <div className="flex items-center gap-2">
              <Input
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    // Close the rename, not the whole sheet.
                    event.stopPropagation();
                    stopEditing();
                  }
                }}
                maxLength={CUSTOMER_TAG_MAX_LENGTH}
                aria-label={t('customers.tags.renameLabel', { tag: tag.name })}
                aria-invalid={error ? true : undefined}
                autoFocus
                className="h-8 w-40"
              />
              <Button type="submit" size="sm" className="h-8" disabled={pending}>
                {t('common.save')}
              </Button>
            </div>
            {error ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            ) : null}
          </form>
        ) : (
          <TagChip name={tag.name} colour={tag.colour} />
        )}
      </td>
      <td className="px-3 py-2 align-middle">
        <ColourSwatch tag={tag} disabled={pending} onChange={(colour) => void onRecolour(colour)} />
      </td>
      <td className="px-3 py-2 align-middle tabular-nums">{tag.customerCount}</td>
      <td className="px-3 py-2 align-middle text-muted-foreground">
        {formatDate(tag.updatedAt)}
        {tag.updatedBy ? <span className="block text-xs">{tag.updatedBy}</span> : null}
      </td>
      <td className="px-3 py-2 align-middle">
        {/* Always there on a phone, which has no hover; on a desk they appear with the row. */}
        <div className="flex justify-end gap-1 sm:opacity-0 sm:transition-opacity sm:focus-within:opacity-100 sm:group-hover:opacity-100">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => setEditing(true)}
            disabled={pending || editing}
            aria-label={t('customers.tags.renameAction', { tag: tag.name })}
          >
            <Pencil aria-hidden="true" className="size-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            onClick={() => void onDelete()}
            disabled={pending}
            aria-label={t('customers.tags.removeAction', { tag: tag.name })}
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
